<template>
  <Layout hide-bottom-nav-bar>
    <template #center-content>
      <BaseCard
        class="neutral tone-100 px-0"
        hide-divider
      >
        <template #header>
          <router-link to="/subscriptions" class="mt-6  d-flex align-center text-decoration-none">
            <BaseButton
              :ripple="false"
              depressed
              color="transparent"
              :height="32"
              class="px-0"
            >
              <font-awesome-icon
                :icon="$vuetify.breakpoint.mdAndDown ?
                  'fa-regular fa-arrow-left': 'fa-regular fa-arrow-left-long'"
                :class="$vuetify.breakpoint.mdAndDown ? 'brand-secondary--text' : 'neutral--text'"
                size="lg"
              />
            </BaseButton>
            <div
              :class="$vuetify.breakpoint.mdAndDown ?
                'brand-secondary--text text-body-m' : 'neutral--text text-label-s-semi-bold'"
              @click="openDialog = false"
            >
              Toutes mes inscriptions
            </div>
          </router-link>
        </template>

        <template #content>
          <div class="px-6">
            <div class="mb-10">
              <BaseHeader
                class="mt-6 mb-6 mt-md-2 mb-md-4 text-center text-md-left"
                :class="$vuetify.breakpoint.mdAndDown ? 'neutral--text' :
                  'brand-secondary--text text--tone-400'"
              >
                <span> Inscription </span>
              </BaseHeader>
            </div>

            <div class="justify-center d-flex">
              <font-awesome-icon
                :icon="statusIcon"
                class="mb-6"
                :class="statusIconColor"
                size="6x"
              />
            </div>

            <BaseSubheader class="brand-primary--text text--tone-400 mb-6 text-center text-md-left">
              <span>
                {{ $t('Your subscription is') }}
              </span>
              <span :class="[subscriptionStatus.textClass]">
                {{ $t(subscriptionStatus.text) }}.
              </span>
              <span v-if="!settings.gradeDisabled && !subscription.data.attributes.rejected_at">
                {{ $t(subscription.data.attributes.graded ?
                  'You are subscribed in the graded class' :
                  'You are subscribed in the ungraded practice') }}
              </span>
            </BaseSubheader>

            <BaseHeaderDescription
              class="mb-6 text-center text-md-left"
              v-if="!subscription.data.attributes.rejected_at"
            >
              <span class="neutral--text text--tone-400">
                {{ $t('CourseInformation', {
                  sport: subscriptionSport.attributes.name,
                  campus: subscriptionCampuses.map((campus) => campus.attributes.name).join(', '),
                  address: subscriptionCourse.attributes.location,
                }) }}
              </span>
            </BaseHeaderDescription>

            <BaseHeaderDescription
              class="mb-6 text-center text-md-left"
              v-if="!subscription.data.attributes.rejected_at"
            >
              {{ $t('It is taught by') }}
              <span class="font-weight-bold">{{ subscriptionTeacher.attributes.name }}.</span>
            </BaseHeaderDescription>

            <div
              class="d-flex justify-md-center"
              v-if="!subscription.data.attributes.rejected_at"
            >
              <BaseButton
                color="error tone-400"
                class="white--text"
                @click="openFilterDialog = true"
                :block="$vuetify.breakpoint.mdAndDown"
              >
                {{ $t('Me désinscrire') }}
              </BaseButton>
            </div>

            <BaseDialog
              :value="openFilterDialog"
              @close="openFilterDialog = false"
              overlay-color="neutral tone-900"
              overlay-opacity="0.96"
              max-width="600"
              content-class=""
            >
              <template #content>
                <BaseCard
                  tile
                  class="neutral tone-100 px-6"
                >
                  <template #header>
                    <BaseSubheader
                      class="my-4"
                    >
                      <span
                        class="py-4 neutral--text text--tone-900"
                      >
                        Confirmation
                      </span>
                    </BaseSubheader>
                  </template>

                  <template #content>
                    <BaseHeaderDescription class="">
                      Cette action est <strong class="red--text text--tone-400">irréversible</strong>,
                      êtes-vous sûr de vouloir
                      vous désinscrire ?
                    </BaseHeaderDescription>
                    <v-row class="pt-4">
                      <v-col cols="6">
                        <BaseButton
                          color="brand-primary tone-400"
                          class="white--text"
                          @click="openFilterDialog = false ; deleteAndRedirect(subscription)"
                          block
                        >
                          Me désinscrire
                        </BaseButton>
                      </v-col>
                      <v-col cols="6">
                        <BaseButton
                          color="red tone-400"
                          class="white--text"
                          @click="openFilterDialog = false"
                          block
                          outlined
                        >
                          Annuler
                        </BaseButton>
                      </v-col>
                    </v-row>
                  </template>
                </BaseCard>
              </template>
            </BaseDialog>
          </div>
        </template>
      </BaseCard>
    </template>
  </Layout>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import Layout from '@/views/containers/Layout.vue';
import BaseCard from '@/components/BaseCard.vue';
import BaseButton from '@/components/BaseButton.vue';
import BaseHeader from '@/components/BaseHeader.vue';
import BaseHeaderDescription from '@/components/BaseHeaderDescription.vue';
import BaseSubheader from '@/components/BaseSubheader.vue';
import BaseDialog from '@/components/BaseDialog.vue';

export default {
  name: 'Subscription',

  data() {
    return {
      data: false,
      openFilterDialog: false,
    };
  },

  components: {
    Layout,
    BaseCard,
    BaseButton,
    BaseHeader,
    BaseHeaderDescription,
    BaseSubheader,
    BaseDialog,
  },

  methods: {
    ...mapActions(['fetchSubscription', 'deleteSubscription']),
    deleteAndRedirect(subscription) {
      this.deleteSubscription(subscription.data).then(() => {
        this.$router.push('/subscriptions');
      });
    },
  },

  computed: {
    ...mapState(['subscription', 'settings']),
    ...mapGetters([
      'subscriptionSport',
      'subscriptionCampuses',
      'subscriptionTeacher',
      'subscriptionCourse',
    ]),
    subscriptionStatus() {
      if (this.subscription.data.attributes.confirmed) {
        return {
          text: 'confirmed',
          textClass: 'green--text text--tone-400',
        };
      }
      if (this.subscription.data.attributes.rejected_at) {
        return {
          text: 'rejected',
          textClass: 'red--text text--tone-400',
        };
      }
      if (!this.subscription.data.attributes.confirmed) {
        return {
          text: 'pending',
          textClass: 'brand-secondary--text text--tone-400',
        };
      }
      return {
        text: 'cancelled',
        textClass: 'red--text text--tone-400',
      };
    },
    statusIcon() {
      if (this.subscription.data.attributes.confirmed) {
        return 'fa-solid fa-circle-check';
      } if (!this.subscription.data.attributes.confirmed
        && !this.subscription.data.attributes.rejected_at) {
        return 'fa-solid fa-circle-question';
      }
      return 'fa-solid fa-circle-xmark';
    },
    statusIconColor() {
      if (this.subscription.data.attributes.confirmed) {
        return 'green--text text--tone-400';
      } if (!this.subscription.data.attributes.confirmed
        && !this.subscription.data.attributes.rejected_at) {
        return 'brand-secondary--text text--tone-400';
      }
      return 'brand-secondary--text text--tone-400';
    },
  },

  created() {
    this.fetchSubscription();
  },
};
</script>
